import React, { useRef } from "react";
import { useState, useEffect, useCallback } from "react";
import { detectBarcodeType, isValidBarcodeCharacter } from "../../../utils/helpers/validation";
import classNames from "classnames";
import { addMessage, MessageType } from "../../../lib/store/reducers/responseMessagesReducer";
import { useDispatch } from "react-redux";
import { Input } from "semantic-ui-react";

import styles from "./BarcodeInput.module.css";

type BarcodeScannerProps = {
  onBarcodeScan: (barcode: string, format: string) => void;
  scanTimeout?: number;
  disabled?: boolean;
};

const UNIQUE_INPUT_IDENTIFIER = "ScanInputFieldAutomatic";
const BarcodeInputAutomatic = ({
  onBarcodeScan,
  scanTimeout = 500,
  disabled,
}: BarcodeScannerProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const onScanHandler = async (barcode: string, format: string) => {
    // if (!format.includes("EAN")) return setIsValid(false);

    try {
      onBarcodeScan(barcode, format);
    } catch (error: any) {
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: barcode,
          content: error.message || "Ett okänt fel inträffade.",
        })
      );
    }
  };

  const [barcode, setBarcode] = useState("");
  const barcodeRef = useRef("");

  const isGlobalComposition = useCallback((target: Element) => {
    return target.nodeName === "INPUT" && target.id !== UNIQUE_INPUT_IDENTIFIER;
  }, []);

  const isValidSubmit = useCallback(
    (key: KeyboardEvent["key"]) => {
      return ["Enter", "Alt"].includes(key) && barcodeRef.current.length > 4;
    },
    [barcodeRef]
  );

  const handleKeyDown = useCallback(
    async (event: KeyboardEvent) => {
      const target = event.target as Element;
      if (isLoading || disabled) return;
      if (isGlobalComposition(target)) return;
      if (isValidSubmit(event.key)) {
        const format = detectBarcodeType(barcodeRef.current);
        setIsLoading(true);

        await onScanHandler(barcodeRef.current, format);
        barcodeRef.current = "";
        setBarcode("");
        setIsLoading(false);
      } else if (isValidBarcodeCharacter(event.key)) {
        barcodeRef.current += event.key;
        setBarcode(barcodeRef.current);
      } else if (event.key === "Backspace") {
        barcodeRef.current = barcodeRef.current.slice(0, -1);
        setBarcode(barcodeRef.current);
      }
    },
    [onScanHandler, isGlobalComposition, isValidSubmit, isLoading]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const classes = classNames([styles["barcode-input"], { [styles["progressive"]]: false }]);

  return (
    <Input
      input={{
        onPaste: async (e: React.ClipboardEvent<HTMLInputElement>) => {
          e.preventDefault();
          e.stopPropagation();
          const pastedText = e.clipboardData.getData("text");
          if (pastedText.length > 4 && pastedText.split("").every(isValidBarcodeCharacter)) {
            const format = detectBarcodeType(pastedText);
            setIsLoading(true);
            await onScanHandler(pastedText, format);
            barcodeRef.current = "";
            setBarcode("");
            setIsLoading(false);
          }
        },
      }}
      size="big"
      className={classes}
      autoComplete="off"
      id={UNIQUE_INPUT_IDENTIFIER}
      autoFocus
      disabled={disabled || isLoading} // || success
      value={barcode}
      icon="barcode"
      iconPosition="left"
    />
  );
};

export default BarcodeInputAutomatic;
